import React, { useEffect, useMemo, useRef, useState } from "react";
import { Lock, Mail } from "lucide-react";
import { tt } from "@tennis-matches/services/i18n";
import { navigateTo } from "@tennis-matches/core/router";
import {
  acquireWebAuthSessionAfterRedirect,
  abandonPasswordRecoveryFlow,
  shouldForcePasswordResetFlow,
  updateAuthUserPassword,
} from "@tennis-matches/services/auth";
import {
  AuthBrandPanel,
  AuthMobileBrand,
  AuthPageLogo,
  AuthPasswordToggle,
  AuthTextField,
  TennisBallIcon,
} from "../components/auth/AuthPageVisuals.jsx";
import { useForgotPasswordRequest } from "../hooks/useForgotPasswordRequest.js";
import "./LoginPage.css";

export function ResetPasswordPage({ state }) {
  const t = useMemo(
    () => tt(state?.app?.locale || "de-DE"),
    [state?.app?.locale],
  );
  const [phase, setPhase] = useState("loading");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const newPasswordInputRef = useRef(null);
  const confirmPasswordInputRef = useRef(null);
  const forgot = useForgotPasswordRequest({ t });

  const syncPasswordInputsFromDom = ({
    mirrorConfirmWhenEmpty = false,
  } = {}) => {
    const nextNewPassword = String(newPasswordInputRef.current?.value || "");
    const nextConfirmPassword = String(
      confirmPasswordInputRef.current?.value || "",
    );

    if (nextNewPassword !== newPassword) {
      setNewPassword(nextNewPassword);
    }

    if (nextConfirmPassword !== confirmPassword) {
      setConfirmPassword(nextConfirmPassword);
      return;
    }

    if (
      mirrorConfirmWhenEmpty &&
      nextNewPassword &&
      !nextConfirmPassword &&
      !confirmPassword
    ) {
      setConfirmPassword(nextNewPassword);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const session = await acquireWebAuthSessionAfterRedirect({
          timeoutMs: shouldForcePasswordResetFlow() ? 20000 : 15000,
          recoveryTimeoutMs: shouldForcePasswordResetFlow() ? 12000 : 8000,
        });
        if (cancelled) return;
        setPhase(session ? "form" : "request-link");
      } catch {
        if (!cancelled) setPhase("request-link");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (phase !== "form") return undefined;
    const intervalId = window.setInterval(() => {
      syncPasswordInputsFromDom({ mirrorConfirmWhenEmpty: true });
    }, 250);
    return () => window.clearInterval(intervalId);
  }, [phase, newPassword, confirmPassword]);

  const onSavePassword = async () => {
    setError("");
    setInfo("");
    const pw = String(newPassword || "");
    const pw2 = String(confirmPassword || "");
    if (pw.length < 8) {
      setError(t("auth.resetPassword.error.tooShort"));
      return;
    }
    if (pw !== pw2) {
      setError(t("auth.resetPassword.error.mismatch"));
      return;
    }
    setBusy("save");
    try {
      const result = await updateAuthUserPassword(pw);
      if (!result?.ok) {
        setError(
          result?.reason === "password-too-short"
            ? t("auth.resetPassword.error.tooShort")
            : result?.reason === "not-authenticated"
              ? t("auth.resetPassword.error.noSession")
              : t("auth.resetPassword.error.failed"),
        );
        return;
      }
      setInfo(t("auth.resetPassword.success"));
      setPhase("done");
    } finally {
      setBusy("");
    }
  };

  const bannerError = phase === "request-link" ? forgot.error : error;
  const bannerInfo =
    phase === "request-link"
      ? forgot.info
      : info && phase !== "done"
        ? info
        : "";

  const body =
    phase === "loading" ? (
      <p className="muted authPage__welcomeSubtitle">
        {t("auth.resetPassword.loading")}
      </p>
    ) : phase === "done" ? (
      <>
        <p className="authPage__message authPage__message--info" role="status">
          {info}
        </p>
        <button
          type="button"
          className="authPage__submitBtn"
          onClick={() => {
            abandonPasswordRecoveryFlow();
            navigateTo("login", true);
          }}
        >
          {t("auth.resetPassword.backToLogin")}
        </button>
      </>
    ) : phase === "request-link" ? (
      <>
        <p className="authPage__welcomeSubtitle">
          {t("auth.forgotPassword.subtitle")}
        </p>
        <div className="authPage__form">
          <AuthTextField
            id="auth-reset-request-email"
            label={t("auth.login.email")}
            type="email"
            value={forgot.email}
            disabled={forgot.busy}
            autoComplete="email"
            leadingIcon={Mail}
            onChange={(e) => forgot.setEmail(String(e.target.value || ""))}
          />
          <button
            type="button"
            className="authPage__submitBtn"
            disabled={forgot.busy}
            onClick={() => void forgot.onSubmit()}
          >
            {forgot.busy
              ? t("auth.forgotPassword.sending")
              : t("auth.forgotPassword.submit")}
          </button>
        </div>
        <p className="authPage__footerSwitch">
          <button
            type="button"
            className="authPage__footerSwitchBtn"
            onClick={() => {
              abandonPasswordRecoveryFlow();
              navigateTo("login", true);
            }}
          >
            {t("auth.forgotPassword.backToLogin")}
          </button>
        </p>
      </>
    ) : (
      <>
        <p className="authPage__welcomeSubtitle">
          {t("auth.resetPassword.subtitle")}
        </p>
        <div className="authPage__form">
          <AuthTextField
            id="auth-reset-new-password"
            name="newPassword"
            label={t("auth.resetPassword.newPassword")}
            type={showNewPassword ? "text" : "password"}
            value={newPassword}
            inputRef={newPasswordInputRef}
            disabled={busy === "save"}
            autoComplete="new-password"
            leadingIcon={Lock}
            trailing={
              <AuthPasswordToggle
                visible={showNewPassword}
                onToggle={() => setShowNewPassword((v) => !v)}
                labelShow={t("auth.login.showPassword")}
                labelHide={t("auth.login.hidePassword")}
              />
            }
            onInput={() =>
              syncPasswordInputsFromDom({ mirrorConfirmWhenEmpty: true })
            }
            onChange={(e) => {
              const nextValue = String(e.target.value || "");
              setNewPassword(nextValue);
            }}
          />
          <AuthTextField
            id="auth-reset-confirm-password"
            name="confirmPassword"
            label={t("auth.resetPassword.confirmPassword")}
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            inputRef={confirmPasswordInputRef}
            disabled={busy === "save"}
            autoComplete="new-password"
            leadingIcon={Lock}
            trailing={
              <AuthPasswordToggle
                visible={showConfirmPassword}
                onToggle={() => setShowConfirmPassword((v) => !v)}
                labelShow={t("auth.login.showPassword")}
                labelHide={t("auth.login.hidePassword")}
              />
            }
            onInput={() => syncPasswordInputsFromDom()}
            onChange={(e) => setConfirmPassword(String(e.target.value || ""))}
          />
          <button
            type="button"
            className="authPage__submitBtn"
            disabled={busy === "save"}
            onClick={() => void onSavePassword()}
          >
            {busy === "save"
              ? t("auth.resetPassword.saving")
              : t("auth.resetPassword.submit")}
          </button>
        </div>
      </>
    );

  return (
    <div className="authPage">
      <div className="authPage__bg" aria-hidden="true" />
      <div className="authPage__inner">
        <header className="authPage__header">
          <AuthPageLogo t={t} />
        </header>

        <AuthMobileBrand t={t} />

        <div className="authPage__card">
          <AuthBrandPanel t={t} />

          <div className="authPage__formCol">
            <div className="authPage__formCard">
              <div className="authPage__welcome">
                <h2 className="authPage__welcomeTitle">
                  <span className="authPage__welcomeIcon" aria-hidden="true">
                    <TennisBallIcon size={22} />
                  </span>
                  {phase === "request-link"
                    ? t("auth.forgotPassword.title")
                    : t("auth.resetPassword.title")}
                </h2>
              </div>

              {bannerError ? (
                <p
                  className="authPage__message authPage__message--error"
                  role="alert"
                >
                  {bannerError}
                </p>
              ) : null}
              {bannerInfo ? (
                <p
                  className="authPage__message authPage__message--info"
                  role="status"
                >
                  {bannerInfo}
                </p>
              ) : null}

              {body}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
